/**
 * O painel de achados de qualquer domínio, sobre qualquer entidade.
 *
 * É o ponto do rastreamento inteiro, e será o de cada domínio que vier: coordenada não é
 * informação — de quem é aquele lugar, é. Como a base já tem o endereço de dezenas de milhares de
 * pessoas e empresas, cada parada pode virar uma frase sobre alguém que já está no cadastro.
 *
 * **A origem da afirmação aparece sempre.** O servidor classifica cada achado em forte ou fraco, e
 * o que veio de palpite fica separado, embaixo, dito como o que é. Antes essa classificação era
 * feita aqui, testando `metodo !== 'logradouro'` — a tela conhecendo por acaso o vocabulário
 * interno do motor de correlação, num teste que o segundo domínio não teria como acertar.
 */
import { Building2, Car, Clock, FileText, Home, MapPin, RefreshCw, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { DOMINIOS } from '@/lib/achados/dominios';
import { frase } from '@/lib/achados/frases';
import type { Achado, RefEntidade, RespostaAchados } from '@/lib/achados/tipos';
import { chaveRef } from '@/lib/achados/tipos';
import { api } from '@/lib/api';
import { rotaEntidade } from '@/lib/recursos';

/**
 * Formata o `quando` do achado respeitando o que ele É.
 *
 * `new Date('2026-06-29')` é meia-noite UTC — em Brasília isso vira **28/06 às 21h**, e o operador
 * lê uma data que não é a do dado. Foi exatamente o que apareceu na primeira vez que a tela rodou
 * com a carteira real: a posição de 29/06 aparecia como 28/06.
 *
 * Data pura (`AAAA-MM-DD`) sai só como data, montada dos pedaços — sem passar pelo `Date`, que não
 * tem como saber que ali não havia hora nenhuma. Achado de rastreamento traz instante com fuso e
 * continua mostrando a hora, que ali é a informação.
 */
function quandoLegivel(iso: string): string {
  const so_data = /^\d{4}-\d{2}-\d{2}$/.exec(iso);
  if (so_data) {
    const [ano, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${ano}`;
  }
  return new Date(iso).toLocaleString('pt-BR');
}

const ICONE: Record<string, LucideIcon> = {
  pessoa: User,
  empresa: Building2,
  condominio: Home,
  imovel: Home,
  veiculo: Car,
  processo: FileText,
};

function Linha({
  a,
  rotulos,
  vias,
}: {
  a: Achado;
  rotulos: Record<string, string>;
  vias: Record<string, { rotulo: string; classe: string }>;
}) {
  const primeira: RefEntidade | undefined = a.entidades[0];
  const Icone = (primeira && ICONE[primeira[0]]) ?? MapPin;
  const via = vias[a.evidencia.via];
  const distancia = a.numeros.distancia_m;
  const endereco = a.evidencia.detalhe.endereco as string | undefined;

  // A frase pede a entidade pelo ÍNDICE em que a cita; quem monta o link é este componente, uma
  // vez só. Assim uma frase nova não reimplementa `rotaEntidade` nem as classes do link.
  const alvo = (i: number) => {
    const ref = a.entidades[i];
    if (!ref) return null;
    return (
      <Link to={rotaEntidade(ref[0], ref[1])} className="font-semibold text-accent hover:underline">
        {rotulos[chaveRef(ref)] ?? ref[1]}
      </Link>
    );
  };

  const texto = frase(a, alvo);
  if (!texto) return null;

  return (
    <div className="flex items-start gap-3 border-t border-border py-3 first:border-t-0">
      <Icone className="mt-1 size-4 shrink-0 text-text-tertiary" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-[15px] leading-snug text-text">{texto}</p>
        <div className="flex flex-wrap items-center gap-2">
          {a.quando && (
            <span className="tipo-corpo-pequeno flex items-center gap-1 text-text-tertiary">
              <Clock className="size-3" />
              {quandoLegivel(a.quando)}
            </span>
          )}
          {a.periodo && (
            <span className="tipo-corpo-pequeno text-text-tertiary">
              {new Date(a.periodo[0]).toLocaleDateString('pt-BR')} —{' '}
              {new Date(a.periodo[1]).toLocaleDateString('pt-BR')}
            </span>
          )}
          {via && (
            <Chip className={via.classe}>
              {via.rotulo}
              {distancia != null ? ` · ${distancia} m` : ''}
            </Chip>
          )}
        </div>
        {endereco && <p className="tipo-corpo-pequeno text-text-tertiary">{endereco}</p>}
      </div>
    </div>
  );
}

export function PainelAchados({
  dominio,
  alvo,
  url,
}: {
  dominio: string;
  /** A entidade sobre a qual perguntar. */
  alvo?: { tipo: string; id: string };
  /**
   * Rota alternativa, para telas que ancoram noutra coisa que não o sujeito — o painel do
   * rastreador pergunta pelo APARELHO, e o sujeito do achado é o veículo onde ele está instalado.
   */
  url?: string;
}) {
  const cfg = DOMINIOS[dominio];
  const endereco =
    url ?? (alvo ? `/cadastro/achados/${alvo.tipo}/${alvo.id}?dominio=${dominio}` : null);
  const [dados, setDados] = useState<RespostaAchados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [executando, setExecutando] = useState(false);

  const carregar = useCallback(async () => {
    if (!endereco) return;
    setCarregando(true);
    const r = await api.get<RespostaAchados>(endereco);
    setCarregando(false);
    if (r.ok) setDados(r.dados);
    else toast.error(r.erro);
  }, [endereco]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!cfg) return null;

  async function executar() {
    if (!cfg.acao) return;
    setExecutando(true);
    const r = await api.post(cfg.acao.endpoint);
    setExecutando(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(cfg.acao.sucesso);
  }

  const achados = dados?.achados ?? [];
  const rotulos = dados?.rotulos ?? {};
  // O que o servidor classificou como fraco fica separado, embaixo, dito como o que é.
  const fortes = achados.filter((a) => a.forca !== 'fraco');
  const fracos = achados.filter((a) => a.forca === 'fraco');
  const Icone = cfg.icone;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icone className="size-4 text-accent" />
          {cfg.titulo}
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">{cfg.subtitulo}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {carregando ? (
          <Spinner />
        ) : !achados.length ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">{cfg.vazio}</p>
        ) : (
          <>
            <div className="flex flex-col">
              {fortes.map((a) => (
                <Linha key={a.chave} a={a} rotulos={rotulos} vias={cfg.vias} />
              ))}
            </div>
            {fracos.length > 0 && (
              <details className="border-t border-border pt-3">
                <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary hover:text-text">
                  {cfg.fracos.rotulo(fracos.length)}
                </summary>
                <p className="tipo-corpo-pequeno mt-2 text-text-tertiary">{cfg.fracos.explicacao}</p>
                <div className="mt-2 flex flex-col">
                  {fracos.map((a) => (
                    <Linha key={a.chave} a={a} rotulos={rotulos} vias={cfg.vias} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
        <div className="flex items-center gap-2 border-t border-border pt-3">
          {cfg.acao && (
            <Button
              size="sm"
              variant="fantasma"
              disabled={executando}
              onClick={() => void executar()}
            >
              {executando ? <Spinner /> : <RefreshCw />} {cfg.acao.rotulo}
            </Button>
          )}
          <Button size="sm" variant="fantasma" onClick={() => void carregar()}>
            Recarregar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
